import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api/client";

const AuthContext = createContext(null);

function readStoredTokens() {
  const access = localStorage.getItem("access");
  const refresh = localStorage.getItem("refresh");

  if (access || refresh) {
    return { access, refresh };
  }

  return null;
}

export function AuthProvider({ children }) {
  const [tokens, setTokens] = useState(readStoredTokens);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(tokens?.access));
  const [isVerifying, setIsVerifying] = useState(Boolean(tokens?.access));
  const [error, setError] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    setTokens(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsVerifying(false);
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (!tokens?.refresh) {
      return null;
    }

    try {
      const response = await apiFetch("/api/token/refresh/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: tokens.refresh }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const newAccess = data?.access;
      if (!newAccess) {
        return null;
      }

      localStorage.setItem("access", newAccess);
      setTokens((prev) => ({
        access: newAccess,
        refresh: prev?.refresh ?? tokens.refresh,
      }));
      setIsAuthenticated(true);
      return newAccess;
    } catch (err) {
      console.error("Failed to refresh token", err); // eslint-disable-line no-console
      return null;
    }
  }, [tokens?.refresh]);

  useEffect(() => {
    let ignore = false;

    async function fetchCurrentUser(accessToken) {
      const response = await apiFetch("/api/auth/me/", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        return { ok: false, status: response.status };
      }

      const data = await response.json();
      return { ok: true, data };
    }

    async function verifySession() {
      if (!tokens?.access) {
        setIsAuthenticated(false);
        setUser(null);
        setIsVerifying(false);
        return;
      }

      setIsVerifying(true);
      setError(null);
      let attemptedRefresh = false;

      try {
        const result = await fetchCurrentUser(tokens.access);

        if (ignore) {
          return;
        }

        if (result.ok) {
          setUser(result.data);
          setIsAuthenticated(true);
          return;
        }

        if (result.status === 401 && tokens?.refresh) {
          attemptedRefresh = true;
          const newAccess = await refreshAccessToken();

          if (!newAccess) {
            if (!ignore) {
              logout();
            }
            return;
          }
          return;
        }

        if (!ignore) {
          logout();
        }
      } catch (err) {
        if (!ignore) {
          setError(err);
          logout();
        }
      } finally {
        if (!ignore && !attemptedRefresh) {
          setIsVerifying(false);
        }
      }
    }

    verifySession();

    return () => {
      ignore = true;
    };
  }, [tokens?.access, tokens?.refresh, logout, refreshAccessToken]);

  const handleLogin = useCallback((access, refresh) => {
    localStorage.setItem("access", access);
    if (refresh) {
      localStorage.setItem("refresh", refresh);
    }

    setTokens({ access, refresh });
    setIsAuthenticated(true);
    setIsVerifying(true);
  }, []);

  const authorizedFetch = useCallback(
    async (input, init = {}) => {
      if (!tokens?.access) {
        throw new Error("No access token available");
      }

      const doFetch = (token) => {
        const headers = {
          ...(init?.headers || {}),
          Authorization: `Bearer ${token}`,
        };
        return apiFetch(input, { ...init, headers });
      };

      let response = await doFetch(tokens.access);
      if (response.status !== 401) {
        return response;
      }

      const newAccess = await refreshAccessToken();
      if (!newAccess) {
        logout();
        return response;
      }

      response = await doFetch(newAccess);
      if (response.status === 401) {
        logout();
      }
      return response;
    },
    [tokens?.access, refreshAccessToken, logout]
  );

  const value = useMemo(
    () => ({
      tokens,
      user,
      isAuthenticated,
      isVerifying,
      error,
      login: handleLogin,
      logout,
      setUser,
      authorizedFetch,
    }),
    [tokens, user, isAuthenticated, isVerifying, error, handleLogin, logout, authorizedFetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
